import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sortByLabel, sortByName, sortByTitle } from "@/lib/sortRows";
import AppLayout from "@/components/AppLayout";
import { PriorityBadge, StatusSelectPill, STATUS_STYLES } from "@/components/StatusBadge";
import AvatarStack from "@/components/AvatarStack";
import DataTable from "@/components/DataTable";
import SlippageCell from "@/components/SlippageCell";
import { DONE_TASK_STATUSES, matchesSchedule, SCHEDULE_FILTERS, slippageSortValue, targetDate } from "@/lib/ageing";
import TableSearch, { matchesQuery } from "@/components/TableSearch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ReportIssueModal from "@/components/ReportIssueModal";
import PendingLiveApprovals from "@/components/PendingLiveApprovals";

// The task_status enum, exactly: Open -> WIP -> Test -> UAT -> Cutover -> Live,
// with Hold as a side-state at any point. Live is the terminal "shipped" state.
// "closed" is the terminal administrative state — finished with, as opposed to
// shipped — and was added to the enum itself by
// supabase/schema_task_status_closed.sql. It is NOT a frontend-only label: the
// Postgres task_status type has to carry it, or every status change to Closed
// comes back as "invalid input value for enum task_status" behind a generic
// "something went wrong" toast (which is exactly what happened when this list
// offered Closed before the enum had it). Keep this list, the enum, and the
// ai-chat edge function's list_tasks status enum in step.
const T_STATUSES = ["open", "wip", "test", "uat", "cutover", "live", "closed", "cancelled", "hold"];

// One entry per column, in default display order. `weight` is a relative
// starting width — DataTable seeds the columns proportionally so they fill the
// container, then the user can drag any edge. `locked` columns can't be hidden
// or dragged: a row with no title isn't identifiable.
/*
  `sortValue` opts a column into header-click sorting (see DataTable). The grid
  cannot derive a sort key from a rendered cell — Assignee(s) is an avatar stack
  and Status is a dropdown pill — so each column states what it sorts on.

  Priority and Status sort by MEANING, not by label: a severity ramp and a
  workflow pipeline respectively, for the same reason lib/sortRows.js refuses to
  alphabetise them. Dates are ISO strings from Postgres, which compare
  correctly as text, and DataTable puts blanks last in both directions so a task
  with no ETA never reads as "the earliest".
*/
const PRIORITY_RANK = { urgent: 4, high: 3, medium: 2, low: 1 };

/*
  Sorts "1.9" before "1.10" and "2" after "1.12".

  A plain string compare puts "1.10" before "1.9", and localeCompare's
  numeric:true only helps per digit-run, which breaks across different depths.
  Comparing segment by segment as numbers is the only ordering that reads as a
  tree: 1, 1.1, 1.2, 1.10, 2.
*/
const numberSortKey = (t) =>
  String(t.task_number || "")
    .split(".")
    .map((part) => String(Number(part) || 0).padStart(6, "0"))
    .join(".");

const COLUMN_DEFS = [
  { key: "number",    label: "#",           cls: "px-3", weight: 84,
    sortValue: numberSortKey },
  { key: "task",      label: "Task",        locked: true, cls: "px-4",
    sortValue: (t) => t.title || "" },
  { key: "project",   label: "Project",     cls: "px-4" },
  { key: "assignees", label: "Assignee(s)", cls: "px-3", weight: 116 },
  { key: "requested",  label: "Requested By", cls: "px-3", weight: 130,
    sortValue: (t) => t.requested_by || "" },
  { key: "priority",  label: "Priority",    cls: "px-3", weight: 100,
    sortValue: (t) => PRIORITY_RANK[t.priority] ?? 0 },
  { key: "status",    label: "Status",      cls: "px-3", weight: 116,
    sortValue: (t) => T_STATUSES.indexOf(t.status) },
  { key: "eta",       label: "ETA",         cls: "px-3", weight: 104,
    sortValue: (t) => t.eta || "" },
  { key: "revised",   label: "Revised ETA", cls: "px-3", weight: 124,
    sortValue: (t) => t.revised_eta || "" },
  // sysdate - NVL(revised_eta, eta), in days, for work still in flight; the
  // delivered variance for work that is done. Sortable, which is the point:
  // one click puts the worst-slipped OPEN work at the top, with everything
  // already finished parked at the bottom.
  { key: "slippage",  label: "Slippage",    cls: "px-3 text-right", weight: 124,
    // First click = most overdue first, which is now the most NEGATIVE
    // days-left figure, so this sorts ASCENDING first.
    sortDefaultDir: "asc",
    sortHint: "Click to sort most overdue first",
    sortValue: (t) => slippageSortValue({ eta: t.eta, revised: t.revised_eta, done: taskDone(t) }) },
  { key: "created",   label: "Date Added",  cls: "px-3", weight: 112,
    sortValue: (t) => t.created_at || "" },
];

// live = shipped, closed = finished with, cancelled = dropped. All three stop
// the clock; only cancelled has no delivery to be early or late for.
const taskDone = (t) => DONE_TASK_STATUSES.includes(t.status);

const COL_STORAGE_KEY = "tasks.visibleColumns";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function TasksPage() {
  const nav = useNavigate();
  const { user: currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [status, setStatus] = useState("all");
  const [project, setProject] = useState("all");
  const [assignee, setAssignee] = useState("all");
  // Outside the Project -> Assignee -> Status cascade on purpose: it asks about
  // dates, not about who or where, so it never narrows or resets the others.
  const [schedule, setSchedule] = useState("all");
  // The date-range half of "asks about dates" — schedule above buckets by
  // ageing (overdue/on-track/etc.), this narrows to an explicit ETA/revised-ETA
  // window instead, for "what's due between the 1st and the 15th" rather than
  // a bucket. Same NVL(revised, eta) target date ageing.js already uses
  // everywhere else (Slippage column, the schedule filter), so a task with a
  // revised ETA is judged by that date, not its now-superseded original one.
  const [etaFrom, setEtaFrom] = useState("");
  const [etaTo, setEtaTo] = useState("");
  const [query, setQuery] = useState("");
  const [issueModal, setIssueModal] = useState({ open: false, entityId: null, entityLabel: "" });

  const isAdmin = currentUser?.role === "admin";
  // "owner" has identical permissions to "manager" everywhere — see
  // is_manager_or_admin() in schema_owner_role.sql.
  const isManager = currentUser?.role === "manager" || currentUser?.role === "owner";

  /*
    The page opens on the viewer's OWN work, and shows them their own corner of
    the portfolio rather than all of it.

    The database already scopes a plain `member` to the projects they own or
    belong to. A `manager` does NOT get that treatment -- the "read tasks"
    policy short-circuits on is_manager_or_admin(), so every manager reads every
    task in the company. That is a deliberate choice recorded in CLAUDE.md, and
    it stays: what changes here is what the Tasks PAGE shows a manager, not what
    the database will hand them. Admin is exempt on purpose; the
    cross-portfolio view is the whole point of that role.

    LIMIT, worth being honest about: this is a view, not a boundary. A manager's
    browser still receives every task, so this narrows what they are shown, not
    what they could reach. Making it a real boundary means dropping
    is_manager_or_admin() from the tasks/projects policies, which changes
    manager behaviour across the entire app -- Dashboard, Projects, Reports and
    digests included -- and is the same class of edit that produced "infinite
    recursion detected in policy" (42P17) twice. That is a separate decision.
  */
  const isMine = (t) => (t.assignee_ids || [t.assignee_id]).includes(currentUser?.id);

  // Applied on the FIRST load only. load() runs again after every status
  // change, and re-applying the default there would snap the grid back to
  // "my tasks" the moment somebody edited a colleague's row.
  const defaultApplied = useRef(false);

  // Mirrors the "update own or manager tasks" RLS policy — a plain member
  // can see every task in a project they belong to (that's the point of
  // the project-membership model) but can only ever change the status of
  // one assigned to them (any of the task's assignees, not just the
  // primary — a task can now have more than one).
  const canEditTask = (t) => isAdmin || isManager || (t.assignee_ids || [t.assignee_id]).includes(currentUser?.id);

  const load = async () => {
    const [t, p, u, pm, ini] = await Promise.all([
      api.get("/tasks"),
      api.get("/projects"),
      api.get("/users"),
      // Which projects the viewer is on. RLS scopes this differently per role:
      // a manager reads every membership row (so it is filtered to themselves
      // below), a member reads only their own. Either way the filter yields
      // the same answer. Tolerated failure -- the page falls back to owner-only
      // scoping rather than going blank.
      api.get("/project-members").catch(() => ({ data: [] })),
      // Initiatives the viewer can see. RLS already scopes this one properly
      // for a manager -- unlike tasks and projects, blanket
      // is_manager_or_admin() access here was deliberately removed -- so this
      // returns the initiatives they own, are a member of, or reach through a
      // project.
      api.get("/initiatives").catch(() => ({ data: [] })),
    ]);
    setTasks(t.data);
    setProjects(sortByTitle(p.data));
    setUsers(sortByName(u.data));
    setMemberships(pm.data || []);
    setInitiatives(ini.data || []);

    // The id is checked before the flag is burned, not after: ProtectedRoute
    // will not render this page until the session is resolved, so it is always
    // there -- but a default that silently never applies is a miserable thing
    // to debug if that ever stops being true.
    if (!defaultApplied.current && currentUser?.id) {
      defaultApplied.current = true;
      // Only when they actually have tasks. Defaulting an empty-handed viewer
      // to their own name paints an empty grid on first open, which reads as a
      // broken page rather than as "nothing is assigned to you".
      if ((t.data || []).some(isMine)) setAssignee(currentUser.id);
    }
  };
  useEffect(() => { load(); }, []);

  const updateTaskStatus = async (t, s) => {
    if (t.status === s) return;
    const previous = tasks;
    // Apply locally first so the row doesn't sit on its old value for the
    // length of a round-trip.
    setTasks((cur) => cur.map((x) => (x._id === t._id ? { ...x, status: s } : x)));
    try {
      // Only the changed field — t itself carries a derived assignee_ids array
      // that isn't a real column, so spreading the whole task back into the
      // PATCH body would send that straight to Postgres and fail.
      await api.patch(`/tasks/${t._id}`, { status: s });
      load();
    } catch (e) {
      setTasks(previous);
      // The whole error, not e.response.data.detail — this client wraps
      // Supabase, whose errors have no .response, so the old form always
      // resolved to undefined and printed a generic "Something went wrong"
      // over the top of the real RLS/constraint message.
      toast.error(formatApiErrorDetail(e));
    }
  };

  /*
    The viewer's own slice: projects they own, projects they are on, and -- so
    their own work can never disappear -- any project holding a task assigned to
    them. A manager can be handed a task in a project they are not formally a
    member of, and "see your own thing by default" has to survive that.
  */
  const myMemberProjectIds = new Set(
    memberships.filter((m) => m.user_id === currentUser?.id).map((m) => m.project_id),
  );
  const projectIdsOfMyTasks = new Set(tasks.filter(isMine).map((t) => t.project_id));

  /*
    Owning an INITIATIVE carries everything under it.

    An initiative owner is accountable for its projects whether or not anybody
    remembered to add them to each one, so scoping them to their own membership
    rows would hide the exact work they are answerable for. owner_ids is the
    multi-owner array added by supabase/schema_initiative_owners.sql; owner_id
    is the original single owner, and both still carry weight.
  */
  const myInitiativeIds = new Set(
    initiatives
      .filter((i) => i.owner_id === currentUser?.id
        || (i.owner_ids || []).includes(currentUser?.id))
      .map((i) => i._id),
  );

  const visibleProjects = isAdmin
    ? projects
    : projects.filter(
        (p) => p.owner_id === currentUser?.id
          || myMemberProjectIds.has(p._id)
          || projectIdsOfMyTasks.has(p._id)
          || (p.initiative_id && myInitiativeIds.has(p.initiative_id)),
      );
  const visibleProjectIds = new Set(visibleProjects.map((p) => p._id));

  // Every count, filter option and row on this page derives from THIS, not from
  // the full task list. scopedTasks already covers the viewer's own tasks,
  // because the projects holding them are in visibleProjects above.
  const scopedTasks = isAdmin ? tasks : tasks.filter((t) => visibleProjectIds.has(t.project_id));

  /*
    Who the Assignee filter may name: the people on the viewer's projects, not
    the staff directory. /users returns everyone -- users_public is readable by
    any signed-in user -- so this list was previously the whole company for a
    member too, which is the half of the request that was broken for everybody
    and not just for managers.

    Assembled from three sources because no single one is readable by every
    role: project owners (on the project rows themselves), the membership
    rosters (a manager sees all of them, a member only their own row), and the
    assignees of tasks in scope (readable by anyone who can read the task). The
    viewer is always included so they can always filter to themselves.
  */
  const peopleOnMyProjects = new Set(currentUser?.id ? [currentUser.id] : []);
  visibleProjects.forEach((p) => { if (p.owner_id) peopleOnMyProjects.add(p.owner_id); });
  memberships.forEach((m) => { if (visibleProjectIds.has(m.project_id)) peopleOnMyProjects.add(m.user_id); });
  scopedTasks.forEach((t) => (t.assignee_ids || [t.assignee_id])
    .filter(Boolean).forEach((uid) => peopleOnMyProjects.add(uid)));
  const scopedUsers = isAdmin ? users : users.filter((u) => peopleOnMyProjects.has(u._id));

  const pMap = Object.fromEntries(projects.map((p) => [p._id, p.title]));
  const uById = Object.fromEntries(users.map((u) => [u._id, { id: u._id, name: u.name || u.email, avatar_url: u.avatar_url }]));

  // Full cascade: Project → Assignee → Status. Each filter's options (and
  // validity) are derived from tasks matching every filter that comes
  // before it — not just Assignee depending on Project like before, Status
  // now depends on both Project and Assignee too, so a stale selection
  // (e.g. a status that doesn't exist in the currently-picked project)
  // can't silently zero out the grid.
  const tasksMatchingProject = (p) => scopedTasks.filter((t) => p === "all" || t.project_id === p);
  const tasksMatchingProjectAndAssignee = (p, a) =>
    tasksMatchingProject(p).filter((t) => a === "all" || (t.assignee_ids || [t.assignee_id]).includes(a));

  const assigneeOptions = project === "all"
    ? scopedUsers
    : scopedUsers.filter((u) => tasksMatchingProject(project).some((t) => (t.assignee_ids || [t.assignee_id]).includes(u._id)));

  const statusOptions = [...new Set(tasksMatchingProjectAndAssignee(project, assignee).map((t) => t.status))]
    .filter((s) => T_STATUSES.includes(s))
    .sort((a, b) => T_STATUSES.indexOf(a) - T_STATUSES.indexOf(b));

  const onProjectChange = (v) => {
    setProject(v);
    const stillValidAssignee = assignee === "all" || tasksMatchingProject(v).some((t) => (t.assignee_ids || [t.assignee_id]).includes(assignee));
    const nextAssignee = stillValidAssignee ? assignee : "all";
    if (!stillValidAssignee) setAssignee("all");
    if (status !== "all" && !tasksMatchingProjectAndAssignee(v, nextAssignee).some((t) => t.status === status)) {
      setStatus("all");
    }
  };

  const onAssigneeChange = (v) => {
    setAssignee(v);
    if (status !== "all" && !tasksMatchingProjectAndAssignee(project, v).some((t) => t.status === status)) {
      setStatus("all");
    }
  };

  /*
    Everything except the status filter.

    The metric tiles ARE the status filter, so they have to be counted over the
    rows that every OTHER filter leaves — project, assignee and the search box.
    Counting them over the status-filtered set would zero every tile but the
    selected one; counting them over all tasks (which is what they did) meant
    picking a project left "TOTAL 147 / LIVE 63" on screen while the grid below
    showed nine rows, so the numbers described a table nobody was looking at.
  */
  const scoped = scopedTasks.filter((t) => {
    if (project !== "all" && t.project_id !== project) return false;
    if (assignee !== "all" && !(t.assignee_ids || [t.assignee_id]).includes(assignee)) return false;
    if (!matchesSchedule(schedule, {
      eta: t.eta, revised: t.revised_eta, done: taskDone(t), completedAt: t.completed_at,
    })) return false;
    // Assignees are searchable by name even though the column renders them as
    // avatars: the name is what a reader knows, and the avatar is only a
    // picture of it.
    const people = (t.assignee_ids || [t.assignee_id]).filter(Boolean).map((uid) => uById[uid]?.name);
    // The requester is searchable too: "what did Deepu ask for" is a question
    // people actually have, and requested_by is free text so it is the only
    // way to ask it.
    return matchesQuery(query, t.title, t.description, pMap[t.project_id], t.requested_by, ...people);
  });

  const filtered = scoped
    .filter((t) => status === "all" || t.status === status)
    .filter((t) => {
      if (!etaFrom && !etaTo) return true;
      // Bare "yyyy-mm-dd" for the comparison either way, so a full
      // timestamptz value in the target date compares correctly against a
      // plain <input type="date"> boundary instead of sorting "later" than
      // its own day just for carrying a time component.
      const target = targetDate(t.revised_eta, t.eta);
      if (!target) return false;
      const day = String(target).slice(0, 10);
      if (etaFrom && day < etaFrom) return false;
      if (etaTo && day > etaTo) return false;
      return true;
    });

  const counts = { all: scoped.length };
  T_STATUSES.forEach((s) => { counts[s] = scoped.filter((t) => t.status === s).length; });

  const renderCell = (key, t) => {
    switch (key) {
      case "number":
        return (
          <span className="font-mono text-xs text-zinc-500 tabular-nums" title={t.parent_task_id ? "Subtask" : undefined}>
            {t.task_number || "—"}
          </span>
        );
      case "task":
        // No max-width: under table-fixed the cell is the constraint, so the
        // title uses every pixel the visible columns leave it.
        //
        // Subtasks are indented by depth, read off the dots in their number, so
        // the hierarchy is visible in a flat grid without a tree widget. The
        // caret is not a control: expanding/collapsing would fight the sort and
        // the pagination, both of which the reader controls.
        return (
          <div
            className="flex items-start gap-1.5"
            style={{ paddingLeft: `${(String(t.task_number || "").split(".").length - 1) * 14}px` }}
          >
            <div className="font-medium break-words min-w-0" title={t.title}>
              {t.parent_task_id && <span className="text-zinc-400 mr-1">↳</span>}
              {t.title}
            </div>
          </div>
        );
      case "project":
        return (
          <Link
            to={`/projects/${t.project_id}`}
            onClick={(e) => e.stopPropagation()}
            className="block break-words text-zinc-600 hover:text-[#0176D3]"
            title={pMap[t.project_id] || ""}
          >
            {pMap[t.project_id] || "—"}
          </Link>
        );
      case "assignees": {
        const people = (t.assignee_ids || [t.assignee_id])
          .filter(Boolean)
          .map((uid) => uById[uid] || { id: uid, name: "Unknown" });
        return <AvatarStack people={people} limit={2} />;
      }
      case "requested":
        return (
          <span className="break-words text-zinc-600" title={t.requested_by || ""}>
            {t.requested_by || "—"}
          </span>
        );
      case "priority":
        return <PriorityBadge priority={t.priority} />;
      case "status":
        return (
          // stopPropagation, or picking a status also opens the task.
          <div onClick={(e) => e.stopPropagation()}>
            <StatusSelectPill
              status={t.status}
              statuses={T_STATUSES}
              disabled={!canEditTask(t)}
              onChange={(v) => updateTaskStatus(t, v)}
              testid={`task-status-${t._id}`}
            />
          </div>
        );
      case "slippage":
        return (
          <SlippageCell
            status={t.status}
            eta={t.eta}
            revised={t.revised_eta}
            completedAt={t.completed_at}
          />
        );
      case "created":
        return <span className="font-mono text-xs text-zinc-600">{fmtDate(t.created_at)}</span>;
      case "eta":
        return <span className="font-mono text-xs text-zinc-600">{fmtDate(t.eta)}</span>;
      case "revised": {
        // The one derived signal worth keeping from the columns themselves: a
        // revised ETA that landed past the date the task was committed to.
        const due = t.due_date ? new Date(t.due_date) : null;
        const revised = t.revised_eta ? new Date(t.revised_eta) : null;
        const slipped = Boolean(due && revised && revised > due);
        return (
          <span
            className="inline-flex items-center gap-1.5"
            title={
              slipped
                ? `Slipped past its due date of ${due.toLocaleDateString()}${t.revised_eta_reason ? ` — ${t.revised_eta_reason}` : ""}`
                : t.revised_eta_reason || undefined
            }
          >
            {slipped && <span className="w-1.5 h-1.5 rounded-full bg-zinc-800 shrink-0" />}
            <span className={`font-mono text-xs ${slipped ? "font-semibold text-zinc-900" : "text-zinc-600"}`}>{fmtDate(t.revised_eta)}</span>
          </span>
        );
      }
      default:
        return null;
    }
  };

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="trk-uppercase-label">Execution</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Tasks</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Order is Project → Assignee → Status: Project narrows the
              options in Assignee (see assigneeOptions), so it has to come
              first for "dependent" to mean anything to the user picking
              filters left-to-right. */}
          <Select value={project} onValueChange={onProjectChange}>
            <SelectTrigger className="rounded-sm w-full sm:w-44" data-testid="task-filter-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              {/* Named for what it actually covers. "All projects" on a list of
                  four out of forty-one invites the reader to conclude the data
                  is missing. */}
              <SelectItem value="all">{isAdmin ? "All projects" : "All my projects"}</SelectItem>
              {visibleProjects.map((p) => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={assignee} onValueChange={onAssigneeChange}>
            <SelectTrigger className="rounded-sm w-full sm:w-44" data-testid="task-filter-assignee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isAdmin ? "All assignees" : "Everyone on my projects"}</SelectItem>
              {/* The viewer's own row is marked: the page opens on it, so it
                  should be obvious that the grid is filtered to them rather
                  than short of data. */}
              {assigneeOptions.map((u) => (
                <SelectItem key={u._id} value={u._id}>
                  {u._id === currentUser?.id ? `${u.name} (me)` : u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={schedule} onValueChange={setSchedule}>
            <SelectTrigger className="rounded-sm w-full sm:w-52" data-testid="task-filter-schedule"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SCHEDULE_FILTERS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm w-full sm:w-36" data-testid="task-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {sortByLabel(statusOptions, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-4 md:px-8 py-6 trk-fade-in space-y-6">
        <PendingLiveApprovals />

        {/* Metrics — these double as the status filter. */}
        {/* Ten tiles now: Total plus nine statuses. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-10 border border-border rounded-sm overflow-hidden bg-white">
          <button
            onClick={() => setStatus("all")}
            data-testid="task-metric-all"
            className={`p-4 text-left border-r border-b sm:border-b-0 border-border transition-colors ${status === "all" ? "bg-[#EFF6FF]" : "hover:bg-zinc-50"}`}
          >
            <div className="trk-uppercase-label text-zinc-500">Total</div>
            <div className="font-display font-extrabold text-3xl mt-1 tabular-nums">{counts.all}</div>
          </button>
          {T_STATUSES.map((s, i) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`task-metric-${s}`}
              className={`p-4 text-left border-b sm:border-b-0 border-border transition-colors ${i < T_STATUSES.length - 1 ? "border-r" : ""} ${status === s ? "bg-[#EFF6FF]" : "hover:bg-zinc-50"}`}
            >
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_STYLES[s].dot }} />
                <span className="trk-uppercase-label text-zinc-500">{STATUS_STYLES[s].label}</span>
              </div>
              <div className="font-display font-extrabold text-3xl mt-1 tabular-nums">{counts[s]}</div>
            </button>
          ))}
        </div>

        <DataTable
          toolbarStart={
            <div className="flex items-center gap-2 flex-wrap">
              <TableSearch
                value={query}
                onChange={setQuery}
                placeholder="Search tasks…"
                label="Search tasks"
                testid="tasks-search"
              />
              {/* ETA/revised-ETA window — the NVL(revised, eta) target date,
                  same as the Slippage column and the schedule filter, so a
                  task with a revised ETA is filtered by that, not its
                  superseded original one. */}
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={etaFrom}
                  onChange={(e) => setEtaFrom(e.target.value)}
                  aria-label="ETA/revised ETA from"
                  data-testid="tasks-eta-from"
                  className="h-8 rounded-sm border border-border px-2 text-xs text-zinc-700 focus:border-[#0176D3] focus:outline-none focus:ring-1 focus:ring-[#0176D3]/30"
                />
                <span className="text-xs text-zinc-400">–</span>
                <input
                  type="date"
                  value={etaTo}
                  onChange={(e) => setEtaTo(e.target.value)}
                  aria-label="ETA/revised ETA to"
                  data-testid="tasks-eta-to"
                  min={etaFrom || undefined}
                  className="h-8 rounded-sm border border-border px-2 text-xs text-zinc-700 focus:border-[#0176D3] focus:outline-none focus:ring-1 focus:ring-[#0176D3]/30"
                />
                {(etaFrom || etaTo) && (
                  <button
                    type="button"
                    onClick={() => { setEtaFrom(""); setEtaTo(""); }}
                    data-testid="tasks-eta-clear"
                    className="text-xs text-zinc-400 hover:text-zinc-700 px-1"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          }
          storageKey="tasks.tableLayout"
          testIdPrefix="tasks"
          columns={COLUMN_DEFS}
          rows={filtered}
          rowKey={(t) => t._id}
          /* Stated explicitly rather than left to the task page's fallback, so
             a task reached from here comes back here even after walking through
             a subtask or two. */
          onRowClick={(t) => nav(`/tasks/${t._id}`, {
            state: { from: { pathname: "/tasks", label: "Tasks" } },
          })}
          pageSize={20}
          emptyMessage="No tasks match this filter."
          cellFor={renderCell}
        />
      </div>

      <ReportIssueModal
        open={issueModal.open}
        onOpenChange={(v) => setIssueModal((s) => ({ ...s, open: v }))}
        entityType="task"
        entityId={issueModal.entityId}
        entityLabel={issueModal.entityLabel}
      />
    </AppLayout>
  );
}
